import React from "react";
import { Link } from "react-router-dom";
import { Heartbeat, Stethoscope, Train, ChatCircleDots, CalendarCheck, ShieldCheck } from "@phosphor-icons/react";
import SEO from "../components/SEO";
import { AdVideo } from "../components/AdVideo";

const WHY = [
  { icon: Stethoscope, t: "استشاريون كبار من مستشفيات جامعة كامبريدج", d: "يشارك أطباؤنا في وضع الإرشادات الوطنية البريطانية لعلاج ارتفاع ضغط الدم، ويجمعون بين الخبرة السريرية والبحث العلمي." },
  { icon: Heartbeat, t: "الفحوصات والاستشارة في زيارة واحدة", d: "يمكن إجراء تخطيط القلب (ECG) وتصوير القلب بالموجات فوق الصوتية (الإيكو) ومراقبة ضغط الدم على مدار 24 ساعة قبل الاستشارة مباشرةً في اليوم نفسه، لتوفير وقتكم وعناء السفر." },
  { icon: ShieldCheck, t: "دفع ذاتي وأسعار واضحة", d: "لا حاجة إلى إحالة من طبيب عام. تظهر التكلفة الكاملة على صفحة الدفع الآمنة قبل الدفع، وأي فحص إضافي يُقدَّم سعره كتابيًا قبل إجرائه." },
];

const SERVICES = ["علاج ارتفاع ضغط الدم", "علاج ارتفاع الكوليسترول والوقاية من أمراض القلب", "تقييم مخاطر أمراض القلب والأوعية الدموية", "تصوير القلب بالموجات فوق الصوتية (الإيكو)", "تخطيط القلب ومراقبة نظم القلب (هولتر)", "مراقبة ضغط الدم المتنقلة لمدة 24 ساعة", "الطب القلبي الأيضي وبرنامج إنقاص الوزن بإشراف طبي"];

const STEPS = [
  { icon: CalendarCheck, t: "احجزوا عبر الإنترنت", d: "اختاروا الخدمات والفحوصات والموعد المناسب، ثم ادفعوا بأمان ببطاقتكم. يُؤكَّد الحجز فور الدفع، وتصلكم رسالة تأكيد ودعوة تقويم." },
  { icon: Train, t: "الوصول إلى العيادة", d: "تقع العيادة في مبنى ACCI داخل مستشفيات جامعة كامبريدج. محطة قطار كامبريدج ساوث على بُعد مسافة قصيرة سيرًا على الأقدام، ويمكن الوصول إليها بسهولة من لندن ومطار ستانستد." },
  { icon: ChatCircleDots, t: "نحن هنا لمساعدتكم", d: "يمكنكم مراسلتنا بالعربية عبر المساعد الإلكتروني على هذه الصفحة، وسيتواصل فريقنا معكم بخصوص أي سؤال طبي." },
];

const openChat = () => document.querySelector('[data-testid="chat-launcher"]')?.click();

export default function ArabicPatients() {
  return (
    <div dir="rtl" lang="ar" data-testid="arabic-page" className="text-right" style={{ fontFamily: "'Noto Naskh Arabic', 'Amiri', serif" }}>
      <SEO title="عيادة كامبريدج للوقاية من أمراض القلب والأوعية الدموية" path="/ar"
        description="رعاية قلبية خاصة في كامبريدج للمرضى من الخارج: استشاريون كبار من مستشفيات جامعة كامبريدج، وفحوصات القلب والاستشارة في زيارة واحدة، وحجز ودفع عبر الإنترنت." />
      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-20 max-w-4xl">
          <div className="text-sm text-bronze-dark">للمرضى من خارج المملكة المتحدة</div>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.25] font-semibold">رعاية قلبية متخصصة في كامبريدج، <span className="text-bronze-dark">بكل وضوح واطمئنان</span></h1>
          <p className="mt-6 text-lg leading-loose text-ink-muted max-w-2xl">عيادة خاصة متخصصة في ضغط الدم والكوليسترول والوقاية من أمراض القلب، يقدّم خدماتها استشاريون كبار من مستشفيات جامعة كامبريدج. نرحّب بالمرضى من دول الخليج والعالم العربي.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/enquiry" className="btn-primary" data-testid="arabic-book-btn">احجز موعدًا</Link>
            <button type="button" onClick={openChat} className="btn-secondary" data-testid="arabic-chat-btn">تحدّث مع مساعدنا الذكي (AI) بالعربية</button>
          </div>
          <p className="mt-3 text-xs text-ink-muted">صفحة الحجز باللغة الإنجليزية، ويمكن للمساعد الإلكتروني مساعدتكم بالعربية خطوة بخطوة.</p>
        </div>
      </section>
      <section data-testid="arabic-video-section" className="container-ccc pt-16 max-w-4xl">
        <h2 className="text-2xl font-semibold">شاهد كيف تتم العملية</h2>
        <p className="mt-2 text-sm text-ink-muted">اضغط للتشغيل مع الصوت. المشاهد تمثيلية.</p>
        <div className="mt-6"><AdVideo lang="ar" /></div>
      </section>
      <section className="container-ccc py-16 grid md:grid-cols-3 gap-6">
        {WHY.map(({ icon: Icon, t, d }) => (
          <div key={t} className="p-7 bg-white border border-stone-200/60 rounded-md">
            <Icon size={28} weight="duotone" className="text-bronze-dark" />
            <h2 className="mt-4 text-xl font-semibold leading-relaxed">{t}</h2>
            <p className="mt-2 text-ink-muted leading-loose">{d}</p>
          </div>
        ))}
      </section>
      <section className="container-ccc pb-16 max-w-4xl">
        <h2 className="text-2xl font-semibold">خدماتنا</h2>
        <ul className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="arabic-services">
          {SERVICES.map((s) => <li key={s} className="p-4 bg-cream-100 rounded-md border border-bronze-light/30">{s}</li>)}
        </ul>
      </section>
      <section className="bg-white border-y border-stone-200/60">
        <div className="container-ccc py-16 grid md:grid-cols-3 gap-8">
          {STEPS.map(({ icon: Icon, t, d }, i) => (
            <div key={t}>
              <div className="flex items-center gap-3"><span className="w-9 h-9 rounded-full bg-bronze-dark text-white grid place-items-center">{i + 1}</span><Icon size={24} weight="duotone" className="text-bronze-dark" /></div>
              <h2 className="mt-4 text-xl font-semibold">{t}</h2>
              <p className="mt-2 text-ink-muted leading-loose">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="container-ccc py-16 max-w-3xl">
        <p className="text-sm text-ink-muted leading-loose">ملاحظة: المساعد الإلكتروني لا يقدّم استشارات طبية. في الحالات الطارئة اتصلوا بالرقم 999 داخل المملكة المتحدة أو برقم الطوارئ المحلي.</p>
        <Link to="/" className="mt-4 inline-block text-bronze-dark underline" lang="en" dir="ltr">English website</Link>
      </section>
    </div>
  );
}
